import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Cell,
} from 'recharts';
import dayjs from 'dayjs';
import api from '../../lib/axios';

const PRESETS = [
  { label: 'Last 7 days',  days: 7  },
  { label: 'Last 30 days', days: 30 },
  { label: 'Last 90 days', days: 90 },
];

function fmt$(cents) { return `$${((cents || 0) / 100).toFixed(0)}`; }
function fmtDate(d)  { return dayjs(d).format('MMM D'); }

function StatTile({ label, value, sub, accent }) {
  return (
    <div className="card p-5">
      <p className="text-xs font-semibold text-ink-muted uppercase tracking-wide">{label}</p>
      <p className={`text-3xl font-bold mt-1 ${accent || 'text-ink'}`}>{value}</p>
      {sub && <p className="text-xs text-ink-muted mt-1">{sub}</p>}
    </div>
  );
}

function CustomTooltip({ active, payload, label, money }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-surface-border rounded-lg shadow-md px-3 py-2 text-xs">
      <p className="font-semibold text-ink mb-1">{fmtDate(label)}</p>
      {payload.map(p => (
        <p key={p.name} style={{ color: p.color }}>
          {p.name}: {money ? fmt$(p.value) : p.value}
        </p>
      ))}
    </div>
  );
}

export default function Reports() {
  const [days, setDays] = useState(30);
  const from = dayjs().subtract(days - 1, 'day').startOf('day').toISOString();
  const to   = dayjs().endOf('day').toISOString();

  const params = { from, to };

  const { data: overview }  = useQuery({ queryKey: ['reports-overview', days],  queryFn: () => api.get('/reports/overview',  { params }).then(r => r.data.data) });
  const { data: revenue }   = useQuery({ queryKey: ['reports-revenue',  days],  queryFn: () => api.get('/reports/revenue',   { params }).then(r => r.data.data) });
  const { data: services }  = useQuery({ queryKey: ['reports-services', days],  queryFn: () => api.get('/reports/services',  { params }).then(r => r.data.data) });
  const { data: staffData } = useQuery({ queryKey: ['reports-staff',    days],  queryFn: () => api.get('/reports/staff',     { params }).then(r => r.data.data) });

  const revenueRows = revenue || [];
  const serviceRows = (services || []).slice(0, 6);
  const staffRows   = (staffData || []).slice(0, 6);

  return (
    <div className="p-6 max-w-6xl space-y-6">
      {/* Header + period picker */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-lg font-semibold text-ink">Reports</h1>
          <p className="text-sm text-ink-secondary mt-0.5">Business performance overview</p>
        </div>
        <div className="flex gap-1 bg-surface-100 rounded-lg p-1">
          {PRESETS.map(p => (
            <button
              key={p.days}
              onClick={() => setDays(p.days)}
              className={`px-3 py-1.5 rounded text-xs font-medium transition-all
                ${days === p.days ? 'bg-white shadow-sm text-ink' : 'text-ink-muted hover:text-ink'}`}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatTile label="Total bookings"   value={overview?.total       ?? '—'} sub="in period" />
        <StatTile label="Revenue"          value={fmt$(overview?.revenue)}       sub="confirmed + completed" accent="text-emerald-600" />
        <StatTile label="Completion rate"  value={overview ? `${overview.completionRate}%` : '—'} sub="of all bookings" />
        <StatTile label="New clients"      value={overview?.newClients  ?? '—'} sub="joined in period" />
      </div>

      {/* Revenue over time */}
      <div className="card p-5">
        <h2 className="text-sm font-semibold text-ink mb-4">Revenue over time</h2>
        {revenueRows.length === 0 ? (
          <div className="h-48 flex items-center justify-center text-ink-muted text-sm">No data for this period.</div>
        ) : (
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={revenueRows} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="revGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%"  stopColor="#2E5BBA" stopOpacity={0.15} />
                  <stop offset="95%" stopColor="#2E5BBA" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="date" tickFormatter={fmtDate} tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
              <YAxis tickFormatter={v => `$${(v/100).toFixed(0)}`} tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} width={52} />
              <Tooltip content={<CustomTooltip money />} />
              <Area type="monotone" dataKey="revenue" name="Revenue" stroke="#2E5BBA" strokeWidth={2} fill="url(#revGrad)" dot={false} />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Bookings over time */}
      <div className="card p-5">
        <h2 className="text-sm font-semibold text-ink mb-4">Booking volume</h2>
        {revenueRows.length === 0 ? (
          <div className="h-48 flex items-center justify-center text-ink-muted text-sm">No data for this period.</div>
        ) : (
          <ResponsiveContainer width="100%" height={180}>
            <BarChart data={revenueRows} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
              <XAxis dataKey="date" tickFormatter={fmtDate} tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} allowDecimals={false} width={28} />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="bookings" name="Bookings" fill="#2E5BBA" radius={[3, 3, 0, 0]} maxBarSize={32} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Bottom row: services + staff */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

        {/* Top services */}
        <div className="card p-5">
          <h2 className="text-sm font-semibold text-ink mb-4">Top services</h2>
          {!serviceRows.length ? (
            <p className="text-sm text-ink-muted">No data.</p>
          ) : (
            <div className="space-y-3">
              {serviceRows.map((s, i) => {
                const max = serviceRows[0].bookings;
                return (
                  <div key={s.name}>
                    <div className="flex items-center justify-between text-xs mb-1">
                      <span className="font-medium text-ink truncate max-w-[60%]">{s.name}</span>
                      <span className="text-ink-muted">{s.bookings} bookings · {fmt$(s.revenue)}</span>
                    </div>
                    <div className="h-1.5 rounded-full bg-surface-100 overflow-hidden">
                      <div
                        className="h-full rounded-full bg-primary transition-all duration-500"
                        style={{ width: `${Math.round((s.bookings / max) * 100)}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Staff performance */}
        <div className="card p-5">
          <h2 className="text-sm font-semibold text-ink mb-4">Staff performance</h2>
          {!staffRows.length ? (
            <p className="text-sm text-ink-muted">No data.</p>
          ) : (
            <div className="space-y-3">
              {staffRows.map(s => {
                const max = staffRows[0].bookings;
                return (
                  <div key={s.name}>
                    <div className="flex items-center justify-between text-xs mb-1">
                      <div className="flex items-center gap-1.5 truncate max-w-[60%]">
                        <span className="w-2 h-2 rounded-full shrink-0" style={{ background: s.color }} />
                        <span className="font-medium text-ink">{s.name}</span>
                      </div>
                      <span className="text-ink-muted">{s.bookings} bookings · {fmt$(s.revenue)}</span>
                    </div>
                    <div className="h-1.5 rounded-full bg-surface-100 overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all duration-500"
                        style={{ width: `${Math.round((s.bookings / max) * 100)}%`, background: s.color }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
